import React, { ReactElement } from 'react';
import Link from 'next/link';
import useWindowSize from 'utils/hooks/useWindowSize';
import { parseTextWithLinksAndBold } from '../../FreightDictionary/helpers/parse-text';

type CountryContent = {
  title: string;
  paragraphs?: string[];
  list?: {
    title: string;
    items: string[];
  };
  summary?: string;
};

type InstructionsProps = {
  country: string;
  countryContent?: CountryContent[] | null;
}

const Instructions = ({ country, countryContent }: InstructionsProps): ReactElement => {
  const { width } = useWindowSize();

  const isCountryWithContent = Boolean(countryContent);

  const renderImage = (sectionName: 'start' | 'steps' | 'become') => {
    if (sectionName === 'start') {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_desktop.png' alt='membership' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_tablet.png' alt='membership' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_mobile.png' alt='membership' />
        );
      }
    } else if (sectionName === 'steps') {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_desktop.png' alt='steps' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_tablet.png' alt='steps' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_mobile.png' alt='steps' />
        );
      }
    } else {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_desktop.png' alt='become' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_tablet.png' alt='become' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_mobile.png' alt='become' />
        );
      }
    }
  };

  return (
    <section className={`freight-forwarder-landing__instructions ${isCountryWithContent ? 'with-content' : ''}`}>
      <div className='container'>
        <div className="freight-forwarder-landing__instructions-content">
          {countryContent ? (
            <>
              {countryContent.map(({ title, paragraphs, list, summary }, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <h2 className="freight-forwarder-landing__instructions-title">
                      {title}
                    </h2>
                    <div>
                      {paragraphs
                        ?
                        paragraphs.map((paragraph, idx) => (
                          <p
                            className="freight-forwarder-landing__instructions-paragraph"
                            key={idx}
                          >
                            {parseTextWithLinksAndBold(paragraph)}
                          </p>
                        ))
                        :
                        null
                      }
                    </div>
                    {list ? (
                      <>
                        {list.title && <p className="freight-forwarder-landing__instructions-list-title">{list.title}</p>}
                        <ul className="freight-forwarder-landing__instructions-list">
                          {list.items.map((item, idx) => (
                            <li
                              className="freight-forwarder-landing__instructions-list-item"
                              key={idx}
                            >
                              {parseTextWithLinksAndBold(item)}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                    {summary
                      ?
                      <p
                        key={idx}
                        className="freight-forwarder-landing__instructions-summary"
                      >
                        {parseTextWithLinksAndBold(summary)}
                      </p>
                      :
                      null
                    }
                  </React.Fragment>
                )
              })}
            </>
          ) : (
            <>
              <div className="freight-forwarder-landing__instructions-section start">
                <div className="freight-forwarder-landing__instructions-image-box start">
                  {renderImage('start')}
                </div>
                <div className="freight-forwarder-landing__instructions-description-box start">
                  <h3 className="freight-forwarder-landing__instructions-title">
                    Start your Digital Freight Alliance membership
                  </h3>
                  <div className="freight-forwarder-landing__instructions-paragraphs">
                    <p className="freight-forwarder-landing__instructions-paragraph">
                      for free to enhance your business and capabilities. Access a network of established freight
                      partners in India and across the world via our Members Directory- essential for navigating
                      the complex regulatory landscape and enjoy Logistics Explorer our free real-time freight
                      calculator.
                      Quote your rates to thousands of customers at any time and get online bookings for FCL, LCL, Air,
                      Road, Rail transport, and more.
                    </p>
                    <p className="freight-forwarder-landing__instructions-paragraph">
                      DFA also offers exert guidance for understanding customs regulations and operational best
                      practices through your personalised account and sales managers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="freight-forwarder-landing__instructions-section steps">
                <div className="freight-forwarder-landing__instructions-description-box steps">
                  <h3 className="freight-forwarder-landing__instructions-title">
                    Join the Digital Freight Alliance (DFA) in four easy steps:
                  </h3>
                  <div className="freight-forwarder-landing__instructions-paragraphs">
                    <ol className="freight-forwarder-landing__instructions-list">
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Choose Membership Tier: Select either Standard or Premium based on your needs,
                        each offering benefits like digital tools and trade finance.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Application: Complete the online form on the DFA website, providing basic business
                        details and your preferred membership.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Submit KYC: Fill out a Know Your Customer form to verify your business identity.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Payment and Activation: Make any necessary payments and activate your membership
                        to begin using DFA resources.
                      </li>
                    </ol>
                    <p className="freight-forwarder-landing__instructions-paragraph">
                      Each step is streamlined to quickly integrate your business into DFA’s global network.
                    </p>
                    <Link className="new-link-block full" href="/membership">
                      Become a Member today
                    </Link>
                  </div>
                </div>
                <div className="freight-forwarder-landing__instructions-image-box steps">
                  {renderImage('steps')}
                </div>
              </div>

              <div className="freight-forwarder-landing__instructions-section become">
                <div className="freight-forwarder-landing__instructions-image-box become">
                  {renderImage('become')}
                </div>
                <div className="freight-forwarder-landing__instructions-description-box become">
                  <h3 className="freight-forwarder-landing__instructions-title">
                    How to Become a Freight Forwarder in {country}
                  </h3>
                  <div className="freight-forwarder-landing__instructions-paragraphs">
                    <p className="freight-forwarder-landing__instructions-paragraph become">
                      Getting a freight forwarder license can be complex. Generally, the process includes
                      the following 5 steps:
                    </p>
                    <ol className="freight-forwarder-landing__instructions-list">
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Research Legal Requirements: Investigate the specific regulations and requirements for
                        freight forwarders in {country}
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Meet Licensing Criteria: Fulfill any necessary conditions for obtaining a license, which
                        may include industry experience, passing relevant exams, and securing appropriate
                        insurance coverage.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Submit Application: Apply for a license through the appropriate government department,
                        providing all required documents and paying any associated fees.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        Processing Time: Allow time for your application to be processed and reviewed, which can vary
                        widely from a few weeks to several months depending on the location and the backlog of
                        applications.
                      </li>
                      <li className="freight-forwarder-landing__instructions-list-item">
                        License Issuance: Once approved, you will receive a license to operate as a freight forwarder,
                        enabling you to offer services like customs clearance, cargo transportation, and warehousing
                        for international freight.
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </>
          )
          }

        </div>
      </div>
    </section>
  );
};

export default Instructions;
